import { all, call, delay, fork, getContext, put, race, select, take, takeEvery, takeLatest, throttle } from 'redux-saga/effects';
import first from 'lodash/first';
import { damzOffline, damzOnline } from '../actions/initialization';
import { deleteStream, initializeIfNeeded } from '@wix/da-shared-react/pkg/Stream/base/actions';
import { selectors, StreamType, withCursor } from '@wix/da-shared-react/pkg/Stream';
import { fetchMessageNext, fetchMessagePrev, incrementMessageSent, markRoomRead, markRoomReadSyncDone, maybeDisplayTypingIndicator, presenceJoin, presenceLeave, requestFocusOnMessage, scrollToRequestedMessage, scrollToRequestedMessageCleanup, scrollToRequestedMessageSuccess, selectRoom, sendMessage, setActiveRoomData, setTypingText, setUserTyping, stopTyping, stopTypingLater, waitForRenderBeforeScrollingToMessage } from '../actions/room';
import { fetchStreamContentsSupabase, takeStreamFetch } from '@wix/da-shared-react/pkg/Stream/helpers';
import ddt from '@wix/da-ddt';
import { addErrorNotification } from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import i18next from 'i18next';
import { getMessageCursor, getRoomStreamId, messagesFetchToMessageItem } from '../helpers';
import { getActiveRoom, getFocusMessageRequest, getLastActiveRoom, getPresence } from '../selectors/room';
import { buffers, eventChannel } from 'redux-saga';
import { getCurrentUser, getIsMobile } from '@wix/da-shared-react/pkg/publicSession/selectors';
import { appVisibilityChange } from '@wix/da-shared-react/pkg/redux/app/actions';
import { getStream } from '@wix/da-shared-react/pkg/Stream/base/selectors';
import { isApiError, requestPuppy } from '@wix/da-http-client';
import { STREAM_WINDOW_SIZE_DESKTOP, STREAM_WINDOW_SIZE_MOBILE } from '../../constants';
import { getConfigKey } from '@wix/da-redux-config/pkg/selectors';
import { getDraftMessage } from '../selectors/drafts';
import { clearDraft } from '../actions/drafts';
import history from '@wix/da-shared-react/pkg/redux/routing/history';
import { FetchType } from '@wix/da-shared-react/pkg/Stream/base/types';
function* createSagaChannel(roomChannel, client, user_id) {
    return eventChannel((emitter)=>{
        roomChannel.on('broadcast', {
            event: 'message'
        }, (data)=>{
            emitter({
                type: 'message',
                data
            });
        }).on('presence', {
            event: 'join'
        }, (data)=>{
            emitter({
                type: 'presence_join',
                data
            });
        }).on('presence', {
            event: 'leave'
        }, (data)=>{
            emitter({
                type: 'presence_leave',
                data
            });
        }).on('presence', {
            event: 'sync'
        }, ()=>{
            const newState = roomChannel.presenceState();
            emitter({
                type: 'presence_sync',
                newState
            });
        }).subscribe(async (status, err)=>{
            if (status !== 'SUBSCRIBED') {
                if (err) {
                    ddt.error('damz', 'Failed to connect to realtime', err);
                }
                return;
            }
            ddt.log('damz', 'CONNECTED to room', roomChannel);
            await roomChannel.track({
                user_id
            });
        });
        return async ()=>{
            await roomChannel.untrack();
            client.removeChannel(roomChannel);
        };
    }, buffers.expanding());
}
function* listenToRoomChannel(room_id) {
    // We need to return here when faking API requests to avoid this saga holding everything up.
    const doFake = yield select(getConfigKey, 'fakeApiRequests');
    if (doFake) {
        return;
    }
    const client = yield getContext('supaClient');
    const currentUser = yield select(getCurrentUser);
    yield client.realtime.setAuth(); // fixes - https://github.com/supabase/realtime/issues/1111
    const roomChannel = client.realtime.channel(room_id, {
        config: {
            private: true,
            broadcast: {
                ack: false,
                self: false
            },
            presence: {
                key: room_id
            }
        }
    });
    const sagaChannel = yield call(createSagaChannel, roomChannel, client, currentUser.useridUuid);
    while(true){
        try {
            const payload = yield race({
                chan: take(sagaChannel),
                switchRoom: take(selectRoom),
                offline: take(damzOffline),
                appVisibility: take(appVisibilityChange),
                setUserTyping: take(setUserTyping)
            });
            // Close the channel when we offline or switch room
            if (payload.offline || payload.switchRoom && payload.switchRoom.payload.room_id !== room_id) {
                sagaChannel.close();
                break;
            }
            // Handle typing events
            if (payload.setUserTyping) {
                // Must keep this in sync with the other track data above (currently we just need to re-include user_id)
                yield roomChannel.track({
                    user_id: currentUser.useridUuid,
                    typing: Date.now()
                });
            }
            // Handle new messages coming in via realtime
            if (payload.chan) {
                // Handle messages
                if (payload.chan.type === 'message') {
                    const msgs = messagesFetchToMessageItem([
                        payload.chan.data.payload
                    ]);
                    yield put(withCursor.actions.insertItemsAtOffsetAndSetCursor({
                        streamId: getRoomStreamId(room_id),
                        items: msgs,
                        newCursor: getMessageCursor(msgs[0].msg.message_id, msgs[0].msg.sent_at),
                        offset: 0
                    }));
                }
                // Store who is present in the room, we use this to optimise the fan-out
                // on the server and this list is sent with the send.
                if (payload.chan.type === 'presence_join') {
                    yield put(presenceJoin(room_id, payload.chan.data.newPresences));
                }
                if (payload.chan.type === 'presence_leave') {
                    yield put(presenceLeave(room_id, payload.chan.data.leftPresences));
                }
                if (payload.chan.type === 'presence_sync') {
                    yield put(maybeDisplayTypingIndicator(payload.chan));
                }
            }
            // Track presence when window is hidden/shown
            if (payload.appVisibility) {
                if (payload.appVisibility.payload.hidden) {
                    yield roomChannel.untrack();
                } else {
                    yield roomChannel.track({
                        user_id: currentUser.useridUuid
                    });
                }
            }
        } catch (err) {
            ddt.error('damz', 'Realtime room processing error', err);
            sagaChannel.close();
        }
    }
}
function* fetchRoom(room_id) {
    const doFake = yield select(getConfigKey, 'fakeApiRequests');
    if (doFake && process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line
        const Chance = require('chance').Chance;
        const chance = new Chance();
        yield put(setActiveRoomData({
            allow_replies: true,
            creator_id: chance.guid(),
            last_message_id: chance.guid(),
            name: chance.word(),
            owner_id: chance.guid(),
            room_id: '1',
            type: 1,
            updated_at: chance.timestamp(),
            user_count: 3
        }));
        return true;
    }
    const client = yield getContext('supaClient');
    const result = yield client.from('room').select().eq('room_id', room_id).single();
    if (result.error) {
        // Set error state
        return false;
    }
    yield put(setActiveRoomData(result.data));
    return true;
}
function* handleSelectRoom(action) {
    const isMobile = yield select(getIsMobile);
    // Clear the previous stream so we don't have many rooms in memory
    const prevSelected = yield select(getLastActiveRoom);
    if (prevSelected && prevSelected !== action.payload.room_id) {
        yield put(deleteStream(getRoomStreamId(prevSelected)));
    }
    // First fetch the room
    // TODO not sure we need this, could add this to the list fetch
    const room = yield fetchRoom(action.payload.room_id);
    if (!room) {
        return;
    }
    history.replace(`/messages/${action.payload.room_id}`);
    // When we select a room it becomes read
    yield put(markRoomRead(action.payload.room_id));
    // Next setup the stream
    const streamId = getRoomStreamId(action.payload.room_id);
    yield put(initializeIfNeeded(streamId, {
        streamType: StreamType.WITH_CURSOR,
        autoFetch: true,
        streamParams: {
            room_id: action.payload.room_id
        },
        itemsPerFetch: 30,
        windowSize: isMobile ? STREAM_WINDOW_SIZE_MOBILE : STREAM_WINDOW_SIZE_DESKTOP,
        hasMore: true,
        hasLess: true,
        fetchNextCallback: fetchMessageNext,
        fetchPreviousCallback: fetchMessagePrev
    }));
    // Listen to the realtime
    yield fork(listenToRoomChannel, action.payload.room_id);
}
function* handleMessageFetchNext({ payload: { streamId } }) {
    const client = yield getContext('supaClient');
    const { cursor, limit, room_id } = yield select((state)=>({
            cursor: withCursor.selectors.getCursor(state, streamId),
            limit: selectors.getItemsPerFetch(state, streamId),
            room_id: selectors.getStreamParam(state, streamId, 'room_id')
        }));
    let headFetch = false;
    yield fetchStreamContentsSupabase({
        streamId,
        fetch: async ()=>{
            if (cursor) {
                const [timestamp, messageId] = cursor.split('|');
                const response = await client.rpc('get_messages', {
                    p_room_id: room_id,
                    p_direction: 'next',
                    p_cursor_sent_at: timestamp,
                    p_cursor_msg_id: messageId,
                    p_limit: limit
                });
                return response;
            } else {
                headFetch = true;
                const response = await client.rpc('get_messages', {
                    p_room_id: room_id,
                    p_direction: 'next',
                    p_limit: limit
                });
                return response;
            }
        },
        getStreamData: (result)=>{
            const numResults = result.length;
            // Something probably wrong or the room is really empty
            if (numResults === 0 || !Array.isArray(result)) {
                return {
                    hasMore: false,
                    items: []
                };
            }
            // When we do initial fetch we know there are no more new rows (give or take)
            if (headFetch) {
                const lastItem = result[numResults - 1];
                const prevCursor = getMessageCursor(lastItem === null || lastItem === void 0 ? void 0 : lastItem.message_id, lastItem === null || lastItem === void 0 ? void 0 : lastItem.sent_at);
                const hasLess = numResults > limit;
                if (numResults > limit) {
                    result.pop();
                }
                return {
                    hasMore: false,
                    hasLess,
                    items: messagesFetchToMessageItem(result),
                    prevCursor
                };
            } else {
                // We are fetching using a cursor going forwards
                const firstItem = result[0];
                // TODO probably need to remove the overfetch when numResults > limit
                return {
                    hasMore: numResults > limit,
                    items: messagesFetchToMessageItem(result),
                    cursor: getMessageCursor(firstItem === null || firstItem === void 0 ? void 0 : firstItem.message_id, firstItem === null || firstItem === void 0 ? void 0 : firstItem.sent_at)
                };
            }
        },
        fakeResponse: getFake(room_id, true, false, limit),
        onFailure: function*(error) {
            ddt.alert('damz', '[damz] Failed to fetch next messages', [
                'damz',
                'db'
            ], error);
            yield put(addErrorNotification(i18next.t('damz:damz.room.messages.fetch.error')));
        }
    });
}
function* handleMessageFetchPrev({ payload: { streamId } }) {
    const client = yield getContext('supaClient');
    const { cursor, limit, room_id, hasMore } = yield select((state)=>({
            hasMore: selectors.hasMore(state, streamId),
            cursor: withCursor.selectors.getPrevCursor(state, streamId),
            limit: selectors.getItemsPerFetch(state, streamId),
            room_id: selectors.getStreamParam(state, streamId, 'room_id')
        }));
    yield fetchStreamContentsSupabase({
        streamId,
        fetch: async ()=>{
            if (cursor) {
                const [timestamp, messageId] = cursor.split('|');
                const response = await client.rpc('get_messages', {
                    p_room_id: room_id,
                    p_direction: 'prev',
                    p_cursor_sent_at: timestamp,
                    p_cursor_msg_id: messageId,
                    p_limit: limit
                });
                return response;
            }
        },
        getStreamData: (result)=>{
            const numResults = result.length;
            const lastItem = result[numResults - 1];
            return {
                hasLess: numResults > limit,
                hasMore,
                items: messagesFetchToMessageItem(result),
                prevCursor: numResults > limit ? getMessageCursor(lastItem === null || lastItem === void 0 ? void 0 : lastItem.message_id, lastItem === null || lastItem === void 0 ? void 0 : lastItem.sent_at) : undefined
            };
        },
        fakeResponse: getFake(room_id, false, true, limit),
        onFailure: function*(error) {
            ddt.alert('damz', '[damz] Failed to fetch less messages', [
                'damz',
                'db'
            ], error);
            yield put(addErrorNotification(i18next.t('damz:damz.room.messages.fetch.error')));
        }
    });
}
function* handleSendMessage() {
    const client = yield getContext('supaClient');
    const room_id = yield select(getActiveRoom);
    const presence = yield select((state)=>getPresence(state, room_id));
    const currentUser = yield select(getCurrentUser);
    const draftMessage = yield select((state)=>getDraftMessage(state, room_id));
    const formData = new FormData();
    formData.append('room_id', room_id);
    formData.append('msg', draftMessage.text);
    formData.append('chatters[]', currentUser.useridUuid);
    // Collect the people currently chatting
    const chatters = Object.keys(presence);
    chatters.forEach((chatter)=>{
        formData.append('chatters[]', chatter);
    });
    // Attachments
    draftMessage.attachments.forEach((att)=>{
        formData.append('attachments[]', JSON.stringify(att));
    });
    const response = yield call(requestPuppy, {
        method: 'post',
        url: 'message/send',
        data: formData
    }, undefined, 'damz');
    if (!response || isApiError(response)) {
        // TODO
        return;
    }
    // Clear the editor
    yield put(clearDraft(room_id));
    yield put(incrementMessageSent());
    // Mark last seen, if you send a message assume its now head for you
    if (response.message) {
        yield client.rpc('mark_last_seen', {
            p_room_id: room_id,
            p_msg_id: response.message.messageId
        });
    }
}
export function* getActiveRoomStream() {
    const activeRoom = yield select(getActiveRoom);
    if (!activeRoom) {
        return;
    }
    return yield select(getStream, activeRoom);
}
function* handleRequestFocusOnMessage(action) {
    var _first;
    const { messageId, sentAt } = action.payload;
    const activeRoomId = yield select(getActiveRoom);
    if (!activeRoomId) {
        return;
    }
    const { items, streamId, itemsPerFetch, hasMore, hasLess } = yield select(getStream, getRoomStreamId(activeRoomId));
    const message = items.find((item)=>item.msg.message_id === messageId);
    // If the item is already present in the stream, we can scroll to it without additional network requests.
    if (message) {
        yield put(scrollToRequestedMessage());
        return;
    }
    const beforeFetchLength = items.length;
    const currentFirstItem = (_first = first(items)) === null || _first === void 0 ? void 0 : _first.msg;
    const fetchType = (currentFirstItem === null || currentFirstItem === void 0 ? void 0 : currentFirstItem.sent_at) && new Date(currentFirstItem.sent_at) > new Date(sentAt) ? FetchType.Next : FetchType.Previous;
    /*
  const client: SupabaseClient<Database, 'damz'> =
    yield getContext('supaClient');
   */ yield fetchStreamContentsSupabase({
        streamId,
        fetchType,
        fetch: async ()=>{
            var _getFake;
            // TODO: Replace with real endpoint
            /*
        const response = await client.rpc('get_messages_around', {
          p_room_id: activeRoomId,
          p_cursor_sent_at: sentAt,
          p_cursor_msg_id: messageId,
          p_limit: itemsPerFetch,
        });
         */ return (_getFake = getFake(activeRoomId, false, false, 30, messageId)({})) !== null && _getFake !== void 0 ? _getFake : {
                data: []
            };
        },
        getStreamData: (results)=>{
            if (!Array.isArray(results) || results.length === 0) {
                return {
                    hasMore: false,
                    items: []
                };
            }
            const firstItem = results[0];
            const lastItem = results[results.length - 1];
            return {
                hasLess,
                hasMore,
                items: messagesFetchToMessageItem(results),
                prevCursor: results.length > itemsPerFetch ? getMessageCursor(lastItem === null || lastItem === void 0 ? void 0 : lastItem.message_id, lastItem === null || lastItem === void 0 ? void 0 : lastItem.sent_at) : undefined,
                cursor: getMessageCursor(firstItem === null || firstItem === void 0 ? void 0 : firstItem.message_id, firstItem === null || firstItem === void 0 ? void 0 : firstItem.sent_at)
            };
        },
        onSuccess: function*(results) {
            let indexesToDelete = undefined;
            if (fetchType === FetchType.Previous) {
                indexesToDelete = [
                    results.length,
                    results.length + beforeFetchLength - 1
                ];
            } else {
                indexesToDelete = [
                    0,
                    beforeFetchLength - 1
                ];
            }
            yield put(waitForRenderBeforeScrollingToMessage(indexesToDelete));
        },
        fakeResponse: getFake(activeRoomId, false, false, itemsPerFetch),
        onFailure: function*(error) {
            ddt.alert('damz', '[damz] Failed to fetch less messages', [
                'damz',
                'db'
            ], error);
            yield put(addErrorNotification(i18next.t('damz:damz.room.messages.fetch.error')));
        }
    });
}
function* handleScrollToRequestedMessageCleanup() {
    const activeRoomId = yield select(getActiveRoom);
    const { indexRangeToDeleteAfterScroll } = yield select(getFocusMessageRequest);
    if (!activeRoomId || indexRangeToDeleteAfterScroll === undefined) {
        yield put(scrollToRequestedMessageSuccess());
        return;
    }
    const streamId = getRoomStreamId(activeRoomId);
    const { items } = yield select((state)=>({
            items: selectors.getItems(state, streamId)
        }));
    yield put(withCursor.actions.deleteItems({
        streamId,
        items: items.slice(indexRangeToDeleteAfterScroll[0], indexRangeToDeleteAfterScroll[1] + 1),
        doNotUpdateNextOffset: true
    }));
    yield put(scrollToRequestedMessageSuccess());
}
/**
 * We queue read marking requests for 500ms, this is used with takeLatest
 * so will gather all quickly fired events and flush last one once 500ms ends.
 */ function* handleMarkRoomReadSync() {
    yield delay(500);
    // const room_ids = yield select(getMarkAsReadQueue);
    // TODO call papi
    yield put(markRoomReadSyncDone());
}
/**
 * See if we need to display typing indication
 */ function* handleTypingIndicator(action) {
    yield delay(500);
    const state = action.payload.state.newState;
    const currentRoom = yield select(getActiveRoom);
    if (!state[currentRoom] || state[currentRoom].length === 0) {
        yield put(stopTyping());
        return;
    }
    const now = Date.now();
    let numTalking = 0;
    state[currentRoom].forEach((entry)=>{
        if (entry.typing && now - entry.typing <= 4000) {
            numTalking++;
        }
    });
    if (numTalking === 0) {
        yield put(stopTyping());
        return;
    }
    const typingText = `${numTalking} people typing`;
    yield put(setTypingText(typingText));
    yield put(stopTypingLater());
}
function* handleStopTypingDelayed() {
    yield delay(5000);
    yield put(stopTyping());
}
/**
 * Handles drawer UI
 */ export function* roomSaga() {
    yield take(damzOnline); // wait for online to happen
    yield all([
        takeEvery(selectRoom, handleSelectRoom),
        takeStreamFetch(fetchMessageNext, handleMessageFetchNext),
        takeStreamFetch(fetchMessagePrev, handleMessageFetchPrev),
        throttle(1000, sendMessage, handleSendMessage),
        takeLatest(markRoomRead, handleMarkRoomReadSync),
        takeLatest(maybeDisplayTypingIndicator, handleTypingIndicator),
        takeEvery(stopTypingLater, handleStopTypingDelayed),
        takeLatest(requestFocusOnMessage, handleRequestFocusOnMessage),
        takeLatest(scrollToRequestedMessageCleanup, handleScrollToRequestedMessageCleanup)
    ]);
}
// Testing stuff below
function getFake(roomId, hasMore, hasLess, limit, seededMessageId) {
    return (options)=>{
        if (process.env.NODE_ENV !== 'production') {
            if (window.doError) {
                return {
                    error: 'invalid_request',
                    errorDescription: 'Not logged in.',
                    status: 'error'
                };
            }
            // eslint-disable-next-line
            const Chance = require('chance').Chance;
            const chance = new Chance();
            // eslint-disable-next-line
            const users = require('@wix/da-sample-data').users;
            const seededMessageIdIndex = 15;
            const possibleAuthors = chance.pickset(users, 3);
            const messages = new Array(limit).fill(0).map((_value, index)=>{
                const author = chance.pickone(possibleAuthors);
                return {
                    message_id: seededMessageIdIndex === index ? seededMessageId : chance.guid(),
                    room_id: roomId,
                    user_id: author.useridUuid,
                    type: 0,
                    format: 0,
                    msg: chance.paragraph(),
                    sent_at: `${chance.timestamp()}`,
                    edit_at: '',
                    msg_username: author.username,
                    msg_usericon: 0,
                    msg_symbol: '',
                    msg_user_id: '',
                    msg_user_banned: false,
                    a_count: 0,
                    b_count: 0,
                    c_count: 0,
                    d_count: 0,
                    e_count: 0,
                    f_count: 0,
                    a_attachment: '',
                    b_attachment: '',
                    c_attachment: '',
                    d_attachment: '',
                    e_attachment: '',
                    f_attachment: ''
                };
            });
            return {
                data: messages
            };
        }
    };
}

//# sourceMappingURL=room.js.map